import { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';

/**
 * Reassuring placeholder shown after a message is sent but before the
 * assistant has started streaming text. The previous UX dropped the
 * user into dead air until the first byte arrived, which felt like
 * the app had frozen — especially on slower turns (~5–15s for
 * tool-heavy plans). Now we render an assistant-side bubble with a
 * rotating phrase that crossfades every 5s, so the user has a clear
 * signal that work is happening.
 *
 * Phrases are deliberately calm and slightly varied — "Thinking…"
 * for the first window, then language that hints progress without
 * over-promising ("Putting it together…"). The longer the user
 * waits, the more "I'm still here" the copy reads.
 */
const PHRASES: ReadonlyArray<string> = [
  'Thinking…',
  'Working on it…',
  'Putting it together…',
  'Almost there…',
  'Still working — hang on…',
];

const PHRASE_INTERVAL_MS = 5000;

export function ThinkingIndicator() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const handle = window.setInterval(() => {
      setIndex((i) => Math.min(i + 1, PHRASES.length - 1));
    }, PHRASE_INTERVAL_MS);
    return () => window.clearInterval(handle);
  }, []);

  return (
    <div className="flex items-center gap-2.5">
      {/* Assistant glyph — matches the size/style of a normal assistant
          message bubble so the indicator reads as a forming reply, not
          a generic spinner. */}
      <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-accent-soft text-accent">
        <Sparkles className="size-3.5" />
      </span>
      <span className="flex items-center gap-2 rounded-2xl border border-line bg-card px-3 py-2 shadow-sm">
        <PulsingDots />
        <span
          // Key on the phrase index so React remounts the span on
          // every change — that's what lets the fade-in animation
          // replay rather than only firing once on mount.
          key={index}
          className="animate-thinking-fade text-sm text-muted-foreground"
        >
          {PHRASES[index]}
        </span>
      </span>
    </div>
  );
}

/**
 * Three dots with a staggered scale/opacity pulse. We rely on CSS
 * keyframes (declared in `global.css`) so the animation runs on the
 * compositor and doesn't fight React for frames.
 */
function PulsingDots() {
  return (
    <span aria-hidden className="flex items-center gap-1">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="size-1.5 rounded-full bg-accent"
          style={{
            animation: 'thinking-dot 1.2s ease-in-out infinite',
            animationDelay: `${i * 160}ms`,
          }}
        />
      ))}
    </span>
  );
}
